import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import _ from 'underscore';
import { submitEvent } from '@bingads-webui-universal/accessibility-helper';
import { WithManagedFocuser } from '@bingads-webui-react/hoc-with-managed-focuser';
import { OverlayTrigger, Tooltip } from '@bingads-webui-react/primitive';
import { withOverflowProp } from '@bingads-webui-react/with-overflow-prop';
import { withSignal, CommonEventParser } from '@bingads-webui-react/hoc-with-signal';
import { ItemAriaRoleType } from './aria-role-config';

class RawListItem extends PureComponent {
  constructor(props) {
    super(props);

    this.onFocusHandler = this.onFocusHandler.bind(this);
    this.onSelected = this.onSelected.bind(this);
  }

  onSelected(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.props.item.disabled) {
      return;
    }
    this.props.onSelect(this.props.item, this.props.index, event);
    this.props.focusThis();
  }

  onFocusHandler() {
    this.props.focusItemCallback(this.props.index);
  }

  renderOriginalItem(forwardedRef) {
    const {
      item,
      index,
      keyValue,
      checkSelected,
      selectedItem,
      renderItem,
      focusableRef,
      ariaRole,
      listSize,
      positionInList,
    } = this.props;

    const isSelected = checkSelected(selectedItem, item);

    const ariaAttributes = {};

    if (ariaRole === ItemAriaRoleType.ListBoxOption) {
      ariaAttributes['aria-selected'] = isSelected;
    } else if (ariaRole === ItemAriaRoleType.ListItem) {
      ariaAttributes['aria-setsize'] = `${listSize}`;
      ariaAttributes['aria-posinset'] = `${positionInList}`;

      if (isSelected) {
        ariaAttributes['aria-current'] = 'page';
      }
    }

    /* eslint-disable jsx-a11y/no-static-element-interactions */
    return (
      <div
        ref={focusableRef}
        role={ariaRole}
        {...ariaAttributes}
        key={keyValue}
        onClick={this.onSelected}
        // we need to bubble up event.
        onKeyDown={event => submitEvent(this.onSelected)(event, event)}
        tabIndex={index === 0 ? '0' : '-1'}
        className={classnames('list-selector-item', {
          selected: isSelected,
          disabled: item.disabled,
        })}
        onFocus={this.onFocusHandler}
      >
        {forwardedRef ? renderItem(item, index, forwardedRef) : renderItem(item, index)}
      </div>
    );
  }

  renderItemWithToolTip() {
    const {
      item,
      isTextOverflow,
      forwardedRef,
    } = this.props;
    return (
      <OverlayTrigger
        key="top"
        placement="top"
        overlay={
          <Tooltip id="tooltip-top" className={isTextOverflow ? '' : 'hide'}>
            {isTextOverflow && item.name}
          </Tooltip>
        }
      >
        {this.renderOriginalItem(forwardedRef)}
      </OverlayTrigger>);
  }

  render() {
    const { forwardedRef } = this.props;
    return forwardedRef ? this.renderItemWithToolTip() : this.renderOriginalItem();
  }
}

RawListItem.defaultProps = {
  onSelect: _.noop,
  selectedItem: undefined,
  focusableRef: null,
  focusThis: _.noop,
  focusItemCallback: _.noop,
  forwardedRef: null,
  isTextOverflow: false,
};

RawListItem.propTypes = {
  item: PropTypes.objectOf(PropTypes.any).isRequired,
  index: PropTypes.number.isRequired,
  keyValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  onSelect: PropTypes.func,
  checkSelected: PropTypes.func.isRequired,
  renderItem: PropTypes.func.isRequired,
  selectedItem: PropTypes.objectOf(PropTypes.any),
  focusableRef: PropTypes.func,
  focusThis: PropTypes.func,
  focusItemCallback: PropTypes.func,
  isTextOverflow: PropTypes.bool,
  forwardedRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ariaRole: PropTypes.string.isRequired,
  listSize: PropTypes.number.isRequired,
  positionInList: PropTypes.number.isRequired,
};

// onSelect sends three params this.props.item, this.props.index, event
// the 3rd one is what we're looking for
// compose a new array with 3rd element and send to CommonEventParser
const onSelectEventParser = e => CommonEventParser([e[2]]);

export const ListItem = WithManagedFocuser(withSignal(RawListItem, {
  onSelect: onSelectEventParser,
}));

export const ListItemWithToolTip = withOverflowProp(WithManagedFocuser(RawListItem));
